import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";
import { Close, Fullscreen, FullscreenExit } from "@mui/icons-material";
import Editor from "../../Editor";

function RichEditorDialog({ open, onClose, value, onChange }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [fullscreen, setFullscreen] = useState(false);

  // Calculate word and character count
  const stats = useMemo(() => {
    if (!value) return { words: 0, characters: 0, charactersNoSpaces: 0 };
    
    // Remove HTML tags for accurate counting
    const text = value.replace(/<[^>]*>/g, " ").trim();
    const words = text.split(/\s+/).filter((w) => w.length > 0).length;
    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, "").length;

    return { words, characters, charactersNoSpaces };
  }, [value]);

  const handleClose = () => {
    setFullscreen(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={fullscreen ? false : "lg"}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          borderRadius: fullscreen ? 0 : "16px",
          background: isDark ? "rgba(26, 26, 26, 0.95)" : "#ffffff",
          height: fullscreen ? "100vh" : "auto",
          maxHeight: fullscreen ? "100vh" : "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pr: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Rich Text Editor
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Stack direction="row" spacing={0.5}>
            <Chip
              label={`${stats.words} words`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: "24px" }}
            />
            <Chip
              label={`${stats.characters} chars`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: "24px" }}
            />
          </Stack>
          <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            <IconButton
              size="small"
              onClick={() => setFullscreen(!fullscreen)}
              sx={{ color: theme.palette.text.primary }}
            >
              {fullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: theme.palette.text.primary }}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: fullscreen ? 3 : 2,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
            borderRadius: "4px",
            "&:hover": {
              background: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        <Box
          sx={{
            minHeight: fullscreen ? "calc(100vh - 200px)" : "500px",
          }}
        >
          <Editor value={value} onChange={onChange} placeholder="Start writing your content here..." />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          p: 2,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Tip: Use the toolbar to format text, insert images, videos, tables, and code blocks
        </Typography>
        <Button onClick={handleClose} variant="contained" color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RichEditorDialog;
